import React, { useMemo, useCallback } from "react"
import {
  Avatar,
  Box,
  Card,
  Stack,
  Typography,
  useTheme,
  Chip,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import get from "lodash/get"

import { getIcon } from "utils/icon"
import {
  getCategoryIcon,
  getFeatureDetail,
  IOccupantDetail,
} from "utils/feature"
import { useTranslation } from "providers/i18n"

import { StoreIcon } from "components/icons"
import { ComingSoonChip, NewShopChip } from "components/chips"

export const VerticalDirectoryCard = ({
  data,
  sx = {},
  endAdornmentSlotClasses = {},
  endAdornmentSlots = {},
  isNewShop = false,
  isComingSoon = false,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const categoryIcon = useMemo(() => {
    const parentCategory = get(data, "properties.local_categories[0]", {})
    return getCategoryIcon(parentCategory)
  }, [data])

  const { name, logo, level, category, isAd } = useMemo(
    () => getFeatureDetail(data) as IOccupantDetail,
    [data]
  )

  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`VerticalDirectoryCard`] || {}
  const {
    root,
    occupantLogo,
    cardContent,
    categoryText,
    titleText,
    titleContainer,
    levelText,
    locationInfoContainer,
    infoContainer,
    chip,
    statusTagContainer,
    newShopChip,
    comingSoonChip,
  } = styleOverrides

  return (
    // TODO:: Render collapse item when hasMultipleLocations (has same name.en)
    <Card sx={deepmerge(root, sx)} onClick={handleClick} {...props}>
      <Box sx={occupantLogo}>
        {!isAd && (isNewShop || isComingSoon) && (
          <Box sx={statusTagContainer}>
            {isNewShop && <NewShopChip sx={newShopChip} />}
            {isComingSoon && <ComingSoonChip sx={comingSoonChip} />}
          </Box>
        )}
        <Avatar src={logo}>
          {getIcon(categoryIcon, null, {
            imageIconProps: {
              solidColorFilterId: "directoryCard_avatar_filter",
            },
          }) || <StoreIcon />}
        </Avatar>
      </Box>
      <Box sx={cardContent}>
        <Box sx={titleContainer}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={titleText}>{t(name)}</Typography>
            {isAd && (
              <Chip
                //@ts-ignore
                variant="contained"
                label={t("sponsored.search_result")}
                sx={chip}
              />
            )}
          </Stack>
        </Box>
        <Stack
          direction="row"
          sx={infoContainer}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={categoryText}>
            {t(category)}
          </Typography>
        </Stack>
        <Stack justifyContent="end" spacing={1} sx={locationInfoContainer}>
          <Typography
            sx={{
              ...levelText,
              paddingRight: !data.hasMultipleLocations && 0,
            }}
            variant="body2"
          >
            {data.hasMultipleLocations
              ? t("Multiple Locations")
              : t("Step Floor", { value: level })}
          </Typography>
        </Stack>
      </Box>
    </Card>
  )
}
